import type { EventProperties } from './types';

/**
 * The server's per-event rules (apps/api/src/models/events.py). The API
 * rejects a whole batch when any one event breaks them, so the SDK checks
 * each event at track() time and drops only the bad one.
 */
const MAX_NAME_LENGTH = 255;
// Python's str.isalnum() plus "_", "-" and space.
const NAME_RE = /^[\p{L}\p{N}_\- ]+$/u;
const MAX_PROPERTIES_KEYS = 100;
const MAX_PROPERTIES_DEPTH = 5;
// Characters of compact JSON, as the server measures it.
const MAX_PROPERTIES_CHARS = 32_768;

/** Why the event would be rejected, or null when it's valid. */
export function invalidEventReason(name: string, properties: unknown): string | null {
  // Code points, like the server (Python's len), not UTF-16 units.
  const nameLength = [...(name ?? '')].length;
  if (nameLength === 0 || nameLength > MAX_NAME_LENGTH) {
    return `event name must be 1-${MAX_NAME_LENGTH} characters`;
  }
  if (!NAME_RE.test(name)) {
    return 'event name may only contain letters, digits, "_", "-" and spaces';
  }
  // Check what the server will receive: the JSON (toJSON applied,
  // undefined dropped), not the live object.
  let json: string;
  let sent: unknown;
  try {
    json = JSON.stringify(properties);
    sent = JSON.parse(json);
  } catch {
    return 'properties must be JSON-serializable';
  }
  if (sent === null || typeof sent !== 'object' || Array.isArray(sent)) {
    return 'properties must be an object';
  }
  if (Object.keys(sent).length > MAX_PROPERTIES_KEYS) {
    return `properties cannot exceed ${MAX_PROPERTIES_KEYS} keys`;
  }
  const stack: [unknown, number][] = [[sent, 1]];
  while (stack.length > 0) {
    const [node, depth] = stack.pop()!;
    if (depth > MAX_PROPERTIES_DEPTH) {
      return `properties cannot nest deeper than ${MAX_PROPERTIES_DEPTH} levels`;
    }
    if (node !== null && typeof node === 'object') {
      for (const child of Object.values(node)) stack.push([child, depth + 1]);
    }
  }
  if (json.length > MAX_PROPERTIES_CHARS && [...json].length > MAX_PROPERTIES_CHARS) {
    return `properties exceed ${MAX_PROPERTIES_CHARS / 1024} KB`;
  }
  return null;
}

// Query/fragment parameters that carry credentials. `code` and `state` only
// count on OAuth-shaped URLs (both present); elsewhere they're usually
// promo codes and regions.
const SECRET_PARAM_RE =
  /token|secret|password|passwd|signature|^sig$|^key$|api_?key|^authorization$|jwt|^session_?id$/i;
const OAUTH_PARAM_RE = /^(code|state)$/i;

/**
 * A URL with credential-looking query/fragment parameters redacted, e.g.
 * OAuth callbacks (`#access_token=…`) and magic links (`?token=…`). Hash
 * routes like `#/settings` are kept.
 */
export function redactUrl(url: string): string {
  const oauth = /[?&#]code=/.test(url) && /[?&#]state=/.test(url);
  return url.replace(/([?&#])([^=&#?]+)=[^&#]*/g, (match, sep: string, rawName: string) => {
    let name = rawName;
    try {
      name = decodeURIComponent(rawName);
    } catch {
      // keep the raw name
    }
    const secret = SECRET_PARAM_RE.test(name) || (oauth && OAUTH_PARAM_RE.test(name));
    return secret ? `${sep}${rawName}=[redacted]` : match;
  });
}
